import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

import Footer from './Footer';
function FounderHubPage({ navigateTo, goBack, isLoggedIn, currentUser, userProfile }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfoPackModal, setShowInfoPackModal] = useState(false);
  const [showBadge, setShowBadge] = useState(userProfile?.show_founder_badge !== false);
  const [showOnWall, setShowOnWall] = useState(userProfile?.show_on_founder_wall !== false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isPaidFounder = userProfile?.is_paid_founder || false;
  const isFounder = userProfile?.is_free_founder || userProfile?.is_paid_founder || false;

  // Update visibility settings in database
  const updateVisibilitySettings = async (field, value) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', currentUser.id);
      
      if (error) throw error;
      console.log(`${field} updated to ${value}`);
    } catch (error) {
      console.error('Error updating visibility:', error);
      alert('Failed to update settings. Please try again.');
    }
  };

  // Submit feedback to database
  const handleSubmitFeedback = async () => {
    if (!feedbackCategory || !feedbackText.trim()) {
      alert('Please select a category and enter your feedback');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Insert feedback into database
      const { data, error } = await supabase
        .from('founder_feedback')
        .insert({
          user_id: currentUser.id,
          is_paid_founder: isPaidFounder,
          category: feedbackCategory,
          feedback_text: feedbackText.trim()
        })
        .select();

      if (error) {
        console.error('Supabase error:', error);
        alert(`Failed to submit feedback: ${error.message}`);
        return;
      }

      // Success
      console.log('Feedback submitted successfully:', data);
      setFeedbackSubmitted(true);
      setFeedbackText('');
      setFeedbackCategory('');
      
      // Reset success message after 3 seconds
      setTimeout(() => setFeedbackSubmitted(false), 3000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if not founder
  if (!isLoggedIn || !isFounder) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Founder Access Only
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#6B7280',
            margin: '0 0 24px 0'
          }}>
            This page is only accessible to Founders.
          </p>
          <button
            onClick={() => navigateTo('founder')}
            style={{
              width: '100%',
              padding: '12px',
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
            }}
          >
            Learn About Founder Programme
          </button>
        </div>
      </div>
    );
  }

  const FounderBadge = ({ type }) => {
    const isFilled = type === 'paid';
    const color = type === 'paid' ? '#B58B2E' : '#2563EB';
    
    return (
      <svg width="20" height="22" viewBox="0 0 16 18" style={{ display: 'inline-block', marginLeft: '8px', verticalAlign: 'middle' }}>
        <path
          d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
          fill={isFilled ? color : 'none'}
          stroke={color}
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '20px 16px 60px' : '40px 20px 80px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Information Pack Modal */}
      {showInfoPackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowInfoPackModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '40px 32px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}>
              Founder Information Pack
            </h2>
            <div style={{
              fontSize: '13px',
              color: '#B58B2E',
              fontWeight: '600',
              marginBottom: '24px'
            }}>
              Founder-Only Access
            </div>

            <div style={{
              fontSize: '15px',
              color: '#4B5563',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Development Philosophy
              </h3>
              <p style={{ margin: '0 0 16px 0' }}>
                SudokuMatch is being built carefully and independently. The focus is on creating a competitive Sudoku platform that respects players' time and intelligence, with no artificial friction or engagement manipulation.
              </p>
              <p style={{ margin: '0 0 24px 0' }}>
                Every feature is considered through the lens of: "Does this make competitive Sudoku better?" If the answer isn't clearly yes, it doesn't get built.
              </p>

              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Roadmap Thinking
              </h3>
              <p style={{ margin: '0 0 16px 0' }}>
                Development priorities are guided by three principles:
              </p>
              <ul style={{
                margin: '0 0 24px 0',
                padding: '0 0 0 20px',
                lineHeight: '1.8'
              }}>
                <li><strong>Integrity first:</strong> Anti-cheat, fair play, and competitive accuracy</li>
                <li><strong>Player experience:</strong> Fast, clean, distraction-free solving</li>
                <li><strong>Community value:</strong> Features that make competition more meaningful</li>
              </ul>

              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                How Founders Shape Development
              </h3>
              <p style={{ margin: '0 0 16px 0' }}>
                Your feedback directly influences prioritization. When Founders consistently highlight a pain point or opportunity, it moves up the roadmap.
              </p>
              <p style={{ margin: '0 0 16px 0' }}>
                Founding Members receive priority review, meaning their feedback is read first and given additional weight in decision-making.
              </p>
              <p style={{ margin: '0' }}>
                Early access to new features allows Founders to test and provide input before wider release, ensuring quality and alignment with player needs.
              </p>
            </div>

            <button
              onClick={() => setShowInfoPackModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#FF8C00',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div style={{
        maxWidth: '700px',
        margin: '0 auto'
      }}>
        {/* Back Button */}
        <button
          onClick={goBack}
          style={{
            position: 'absolute',
            top: isMobile ? '20px' : '32px',
            left: isMobile ? '16px' : '32px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '20px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          ←
        </button>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '12px'
          }}>
            <h1 style={{
              fontSize: isMobile ? '32px' : '42px',
              fontWeight: '800',
              margin: '0',
              letterSpacing: '-0.5px'
            }}>
              Founder Hub
            </h1>
            {isPaidFounder && <FounderBadge type="paid" />}
            {!isPaidFounder && <FounderBadge type="free" />}
          </div>
          <p style={{
            fontSize: '15px',
            opacity: 0.9,
            margin: '0',
            fontWeight: '500'
          }}>
            {isPaidFounder ? 'Founding Member Access' : 'Founder Access'}
          </p>
        </div>

        {/* Founder Message */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 24px' : '36px 32px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Thank You for Your Support
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#4B5563',
            lineHeight: '1.7',
            margin: '0 0 16px 0'
          }}>
            You are an early supporter of SudokuMatch, and your presence matters. This platform is being built with your input — not as a marketing promise, but as a genuine commitment to collaborative development.
          </p>
          <p style={{
            fontSize: '15px',
            color: '#4B5563',
            lineHeight: '1.7',
            margin: '0'
          }}>
            The feedback you provide below directly shapes what gets built next. Thank you for being part of this journey.
          </p>
        </div>

        {/* Visibility Settings */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '28px 32px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 20px 0'
          }}>
            Visibility Settings
          </h2>

          {/* Badge Toggle - For All Founders */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '16px',
            borderBottom: isPaidFounder ? '1px solid #E5E7EB' : 'none',
            marginBottom: isPaidFounder ? '16px' : '0'
          }}>
            <div>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '4px'
              }}>
                Founder Badge Visible
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6B7280'
              }}>
                Show your founder badge on leaderboards
              </div>
            </div>
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '50px',
              height: '28px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={showBadge}
                onChange={(e) => {
                  setShowBadge(e.target.checked);
                  updateVisibilitySettings('show_founder_badge', e.target.checked);
                }}
                style={{ display: 'none' }}
              />
              <span style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: showBadge ? '#10B981' : '#D1D5DB',
                borderRadius: '28px',
                transition: 'all 0.3s ease'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '',
                  height: '22px',
                  width: '22px',
                  left: showBadge ? '25px' : '3px',
                  bottom: '3px',
                  background: 'white',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></span>
              </span>
            </label>
          </div>

          {/* Founder Wall Toggle - For Founding Members Only */}
          {isPaidFounder && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '4px'
                }}>
                  Username Visible on Founders Wall
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6B7280'
                }}>
                  Display your name on the public Founders Wall
                </div>
              </div>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '50px',
                height: '28px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={showOnWall}
                  onChange={(e) => {
                    setShowOnWall(e.target.checked);
                    updateVisibilitySettings('show_on_founder_wall', e.target.checked);
                  }}
                  style={{ display: 'none' }}
                />
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: showOnWall ? '#10B981' : '#D1D5DB',
                  borderRadius: '28px',
                  transition: 'all 0.3s ease'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '22px',
                    width: '22px',
                    left: showOnWall ? '25px' : '3px',
                    bottom: '3px',
                    background: 'white',
                    borderRadius: '50%',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}></span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Founding Member Differentiation */}
        {isPaidFounder && (
          <div style={{
            background: 'rgba(181, 139, 46, 0.1)',
            border: '1px solid rgba(181, 139, 46, 0.3)',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <span style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#92400E'
              }}>
                Founding Member Benefits
              </span>
              <FounderBadge type="paid" />
            </div>
            <ul style={{
              margin: '0',
              padding: '0 0 0 20px',
              fontSize: '14px',
              color: '#78350F',
              lineHeight: '1.8'
            }}>
              <li>Your feedback receives priority review</li>
              <li>Early access to new features (coming soon)</li>
              <li>Your name will be included on the Founder Wall</li>
            </ul>
          </div>
        )}

        {/* Discord Server Access */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 24px' : '36px 32px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Join the Founders Discord
          </h2>
          
          <p style={{
            fontSize: '15px',
            color: '#6B7280',
            lineHeight: '1.6',
            margin: '0 0 24px 0',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            Connect with fellow founders, share strategies, get early feature previews, and help shape the future of SudokuMatch in our exclusive community.
          </p>

          <a
            href="https://discord.gg/aDp6UqgmPz"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '16px 40px',
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 0, 0.5)';
              e.target.style.background = '#E07B00';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 0, 0.4)';
              e.target.style.background = '#FF8C00';
            }}
          >
            Join Discord Server →
          </a>
        </div>

        {/* Feedback Submission Form */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 24px' : '36px 32px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 20px 0'
          }}>
            Share Your Feedback
          </h2>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Category
            </label>
            <select
              value={feedbackCategory}
              onChange={(e) => setFeedbackCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '15px',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">Select a category...</option>
              <option value="UX">User Experience</option>
              <option value="Puzzle Quality">Puzzle Quality</option>
              <option value="Ideas">Feature Ideas</option>
              <option value="Bugs">Bugs</option>
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Your Feedback
            </label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Share your thoughts, suggestions, or concerns..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '15px',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </div>

          <button
            onClick={handleSubmitFeedback}
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '14px',
              background: isSubmitting ? '#9CA3AF' : '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>

          {/* Success Message */}
          {feedbackSubmitted && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: '#D1FAE5',
              border: '1px solid #10B981',
              borderRadius: '8px',
              color: '#065F46',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              ✓ Feedback submitted successfully!
            </div>
          )}
        </div>

        {/* Founder Information Pack */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 24px' : '36px 32px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 12px 0'
          }}>
            Founder Information Pack
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: '0 0 20px 0',
            lineHeight: '1.6'
          }}>
            Read about the development philosophy, roadmap thinking, and how Founders influence what gets built.
          </p>
          <button
            onClick={() => setShowInfoPackModal(true)}
            style={{
              padding: '12px 28px',
              background: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
            }}
          >
            Read Information Pack
          </button>
        </div>
      </div>

      <Footer navigateTo={navigateTo} />
    </div>
  );
}

export default FounderHubPage;
